import { useAdmin } from "@/features/experts/hooks/useAdmin";
import { useNutritionist } from "@/features/experts/hooks/useNutritionist";
import { usePhysician } from "@/features/experts/hooks/usePhysician";
import { useSpecialist } from "@/features/experts/hooks/useSpecialist";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import SessionExpiredDialog from "../../../pages/new-platform/components/SessionExpiredDialog";
import { getBloodTrackerByUserId } from "../../../utils/apis/dna-test-tracker";
import { getAnswersByUserId } from "../../../utils/apis/emr-service";
import { getMealPlans } from "../../../utils/apis/plan-service";
import { useAuthStore } from "../../../utils/stores/useAuthStore";
import { usePlansStore } from "../../../utils/stores/usePlansStore";
import { useProductsStore } from "../../../utils/stores/useProductsStore";
import { useSurveyDataStore } from "../../../utils/stores/useSurveyDataStore";
import { useUserStore } from "../../../utils/stores/useUserStore";
import NavBar from "../../navbar/components/NavBar";
import { NavBarElement } from "../../navbar/types/NavBarElement";
import { ElementImportance } from "../types/ElementImportance";
import MobileTopBar from "./MobileTopBar";

interface PlatformPageProps {
    currentItem: NavBarElement;
    children: React.ReactNode;
    hideOnMobile?: boolean;
    importance?: ElementImportance;
    title?: string,
    backTo?: string,
    backButtonText?: string,
}

const PlatformPageLayout: React.FC<PlatformPageProps> = ({
    currentItem,
    children,
    hideOnMobile = false,
    importance = ElementImportance.NoNavBarComponent,
    title = "",
    backTo = "",
    backButtonText = "",
}) => {
    // const { fetchDnaTracker } = useDnaTracker()
    // const { dnaTracker, updateTracker} = useDnaTracker()
    const { auth } = useAuthStore()
    const idToken = auth.idToken
    const userId = auth.userId
    const { userData } = useUserStore()
    // const name = userData.name
    const email = userData.email
    // const { consultsData, updateConsultsData } = useConsultsStore()
    // const { experts } = useExpertsStore()
    const { fetchNutritionist } = useNutritionist()
    const { fetchAdmin } = useAdmin()
    const { fetchSpecialist } = useSpecialist()
    const { fetchPhysician, sessionExpired } = usePhysician()
    // const nutritionist = experts.nutritionist
    // const specialist = experts.specialist
    // const physician = experts.physician
    const { updatePlans } = usePlansStore()
    // const { dnaTestTracker, updateDNATestTracker } = useDNATestTracker()
    // const status = dnaTestTracker.status
    // const { updateBloodTestTracker } = useBloodTestTracker()
    // const { trackers, updateTrackers } = useTrackersStore()
    // const dnaStatus = trackers.dnaStatus
    const { updateSurveyData } = useSurveyDataStore()
    const { productsData } = useProductsStore()
    const membership = productsData.membership
    const metabolicHook = productsData.metabolicHook

    const [shouldShowExpirySessionDialog, setShouldShowExpirySessionDialog] = useState(false)

    const contentRef = useRef<HTMLDivElement>(null);

    // const [isLoading, setIsLoading] = useState(false)

    const handleSessionExpiry = () => {
        if (!shouldShowExpirySessionDialog) {
            setShouldShowExpirySessionDialog(true);
        }
    };

    const fetchMealPlan = async () => {
        if (!idToken || !userId) return
        try {
            const mealPlans = await getMealPlans(userId, idToken)
            if (mealPlans) {
                updatePlans({ mealPlans })
            } else {
                console.warn(`Nessun meal plan per user: ${userId}`)
            }
        } catch (error) {
            if (error instanceof Error) {
                if (error.message.includes("Session expired")) {
                    throw new Error("Session expired");
                } else if (error.message.includes("Nessun meal plan trovato")) {
                    throw new Error(error.message);
                }
            } else {
                throw new Error("Unexpected error on get appointments");
            }
            updatePlans({ mealPlans: null });
        }
    }

    const fetchBloodTracker = async () => {
        if (!userId || !idToken) return;

        try {
            const trackers = await getBloodTrackerByUserId(userId, idToken);
            if (trackers) {
                const newBloodStatus = trackers[0].tracking_status;
                // updateTrackers({ bloodStatus: newBloodStatus });
            } else {
                console.warn(`Blood tracker non trovato per user: ${userId}`)
            }
        } catch (error) {
            console.error(
                "Error fetching trackers:",
                error instanceof Error ? error.message : error
            );
        }
    }

    const getAnswers = async () => {
        if (!userId || !idToken) return;
        try {
            const answers = await getAnswersByUserId(userId, idToken)
            if (answers && answers.length > 0) {
                updateSurveyData({ allAnswers: answers })
            }
        } catch (error) {
            console.error(
                "Error fetching answers:",
                error instanceof Error ? error.message : error
            );
        }
    }

    // const fetchAllData = async () => {
    //     if (!idToken || !userId || !email) return
    //     setIsLoading(true)
    //     try {
    //         await Promise.all([
    //             fetchNutritionist(),
    //             fetchAdmin(),
    //             fetchSpecialist(),
    //             fetchPhysician(),
    //             fetchMealPlan(),
    //             fetchBloodTracker(),
    //             getAnswers(),
    //         ])
    //     } catch (error) {
    //         if (error instanceof Error) {
    //             if (error.message.includes("Session expired")) {
    //                 console.error("Session expired detected in fetchAllData.");
    //                 handleSessionExpiry();
    //             } else if (error.message.includes("Nessun appuntamento trovato")) {
    //                 console.error(error);
    //             } else {
    //                 console.error("An unknown error occurred:", error);
    //             }
    //         } else {
    //             console.error("An unknown error occurred:", error);
    //         }
    //     } finally {
    //         setIsLoading(false);
    //     }
    // }

    useEffect(() => {
        // fetchDnaTracker()
        // fetchAllData()
        // const intervalId = setInterval(fetchAllData, 1 * 60 * 1000)
        // return () => clearInterval(intervalId)
    }, [])

    const newLayout = currentItem === NavBarElement.Home && ((membership &&
        (membership.sub_type === "sub-metabolic-health" || membership.sub_type === "sub-metabolic-health-lite")) || (metabolicHook && metabolicHook === true))
        && !hideOnMobile

    // ********************************************************************************

    const location = useLocation()
    const path = location.pathname
    const isChat = path.startsWith("/chat-session")

    return (
        <div className="w-full h-[100dvh] flex flex-col-reverse md:flex-row">
            <div className={`${(hideOnMobile || importance === ElementImportance.NoNavBarComponent) && "hidden md:block"}`}>
                <NavBar
                    currentItem={currentItem}
                    disabled={hideOnMobile}
                />
            </div>
            <div
                ref={contentRef}
                className={`flex-1 h-full flex flex-col gap-lg md:pt-[70px] overflow-y-auto scrollbar-hide ${!isChat && "px-[10px] pt-[50px] md:px-[50px]"} ${newLayout ? "md:pt-0" : "md:pt-[20px]"}`}
            >
                    {importance !== ElementImportance.NoNavBarComponent && <MobileTopBar importance={importance} backTo={backTo} backButtonText={backButtonText} title={title}/>}
                    {children}
            </div>
            {
                (shouldShowExpirySessionDialog || sessionExpired) &&
                <SessionExpiredDialog />
            }
        </div>
    );
};

export default PlatformPageLayout;
